import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Carousel — horizontal scrolling row of square post tiles.
 * Props:
 *   title?: string
 *   images: string[]
 *   onImageClick?: (idx: number) => void
 */
export const Carousel = ({ title, images = [], onImageClick }) => {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [images.length, updateScrollState]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const step = el.clientWidth * 0.7;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  if (!images.length) return null;

  return (
    <section className="relative" data-testid="carousel-section">
      {title && (
        <h3
          className="font-marker text-xl md:text-2xl text-white uppercase tracking-wide mb-4"
          data-testid="carousel-title"
        >
          {title}
        </h3>
      )}

      <div className="relative group/carousel">
        {/* Left arrow */}
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          disabled={!canScrollLeft}
          aria-label="Scroll left"
          data-testid="carousel-prev"
          className={`hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/70 border-2 border-white/35 text-white items-center justify-center backdrop-blur-sm transition-all ${
            canScrollLeft
              ? 'opacity-0 group-hover/carousel:opacity-100 hover:border-[color:var(--punk-red)] hover:text-[color:var(--punk-red)]'
              : 'opacity-0 pointer-events-none'
          }`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Right arrow */}
        <button
          type="button"
          onClick={() => scrollBy(1)}
          disabled={!canScrollRight}
          aria-label="Scroll right"
          data-testid="carousel-next"
          className={`hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/70 border-2 border-white/35 text-white items-center justify-center backdrop-blur-sm transition-all ${
            canScrollRight
              ? 'opacity-0 group-hover/carousel:opacity-100 hover:border-[color:var(--punk-red)] hover:text-[color:var(--punk-red)]'
              : 'opacity-0 pointer-events-none'
          }`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Scroller */}
        <div
          ref={scrollerRef}
          className="flex gap-3 md:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 -mx-1 px-1"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {images.map((src, idx) => (
            <button
              key={`post-${idx}`}
              type="button"
              onClick={() => onImageClick && onImageClick(idx)}
              data-testid={`carousel-item-${idx}`}
              className="relative flex-none w-[44%] sm:w-[30%] md:w-[24%] lg:w-[19%] aspect-square overflow-hidden snap-start group cursor-zoom-in"
              style={{
                boxShadow:
                  '0 12px 28px rgba(0,0,0,0.45), 0 3px 8px rgba(0,0,0,0.3)',
              }}
            >
              <img
                src={src}
                alt={`Post ${idx + 1}`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                style={{ filter: 'saturate(0.95) contrast(1.04)' }}
              />
              {/* Paper grain overlay */}
              <div
                className="absolute inset-0 pointer-events-none opacity-20"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' seed='3'/><feColorMatrix values='0 0 0 0 0.1  0 0 0 0 0.08  0 0 0 0 0.06  0 0 0 0.32 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
                  mixBlendMode: 'overlay',
                }}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
